import React, { memo, useCallback } from 'react';

import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';

import type { GlobalShortcutComponentItemBase, MappedGlobalShortcutsItem } from '../../types';
import { getUrlMaybe } from '../../utils/get-url-maybe';
import { MenuComponentItem } from '../menu-component-item';
import { fireMenuLinkItemClickAnalyticsEvent, MenuLinkItem } from '../menu-link-item';

import { messages } from './messages';

export type ShortcutMenuItemProps = {
	// This component is exported from the package, which means we can't rely on the context provider that Global Shortcuts uses to determine if Drag & Drop is enabled, as consumers might be using something else.
	// Thus, this value needs to be provided as a prop by whatever is rendering it.
	dragAndDropEnabled?: boolean;
	isInMoreMenu?: boolean;
	openInSameTab?: boolean;
	product?: string;
	shortcut: MappedGlobalShortcutsItem;
};

/** Unmemoized implementation; exported for tests that assert render counts. */
export function ShortcutMenuItemInner({
	shortcut,
	dragAndDropEnabled,
	product,
	openInSameTab,
	isInMoreMenu,
}: ShortcutMenuItemProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const analyticsName = shortcut.analyticsName || `unknown_${shortcut.label}`;
	const ItemWrapper = shortcut?.ItemWrapper ?? React.Fragment;

	const customComponentDraggableOnClick = useCallback(() => {
		fireMenuLinkItemClickAnalyticsEvent(createAnalyticsEvent, analyticsName);
	}, [analyticsName, createAnalyticsEvent]);

	const customComponentOnClick = useCallback(() => {
		fireMenuLinkItemClickAnalyticsEvent(createAnalyticsEvent, analyticsName);
		shortcut.onClick?.();
	}, [analyticsName, createAnalyticsEvent, shortcut]);

	if (shortcut.type === 'component') {
		if (fg('navx-3539-custom-shortcut-component-draggable')) {
			return (
				<MenuComponentItem
					shortcut={shortcut}
					dragAndDropEnabled={dragAndDropEnabled}
					isInMoreMenu={isInMoreMenu}
					onClick={customComponentDraggableOnClick}
				/>
			);
		}

		const Component = shortcut.component as GlobalShortcutComponentItemBase['component'];
		return (
			<ItemWrapper>
				<Component onClick={customComponentOnClick} isInMoreMenu={isInMoreMenu} />
			</ItemWrapper>
		);
	}

	if (shortcut.type === 'internal') {
		return (
			<ItemWrapper>
				<MenuLinkItem
					analyticsName={analyticsName}
					key={shortcut.key}
					icon={shortcut.icon}
					url={getUrlMaybe(shortcut)}
					label={shortcut.label}
					labelElem={shortcut.labelElem}
					isReorderable={shortcut.isReorderable}
					openInSameTab={true}
					onClick={shortcut.onClick}
					onRightClick={shortcut.onRightClick}
					product={product}
					isSelected={shortcut.isSelected}
				/>
			</ItemWrapper>
		);
	}

	return (
		<ItemWrapper>
			<MenuLinkItem
				optionalRef={shortcut.ref}
				analyticsName={analyticsName}
				key={shortcut.key}
				icon={shortcut.icon}
				url={getUrlMaybe(shortcut)}
				label={shortcut.label}
				labelElem={shortcut.labelElem}
				actions={shortcut.actions}
				ariaLabel={formatMessage(messages.menuLinkItemAriaLabel, {
					appName: shortcut.label,
					siteName: '',
				})}
				onClick={shortcut.onClick}
				isReorderable={shortcut.isReorderable}
				onRightClick={shortcut.onRightClick}
				onMouseEnter={shortcut.onMouseEnter}
				onFocus={shortcut.onFocus}
				menuItemId={shortcut.menuItemId}
				product={product}
				openInSameTab={openInSameTab}
				optionsMenuElem={shortcut.optionsMenuElem}
			/>
		</ItemWrapper>
	);
}

export const ShortcutMenuItem = memo(ShortcutMenuItemInner);
